import { SCAN_MODELS, DEFAULT_SCAN_MODEL_KEY } from '@cf-shared/scan-models';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';

export const DEFAULT_SCAN_MODEL = DEFAULT_SCAN_MODEL_KEY;

/** Minimal shape of a scan-model registry entry — every SCAN_MODELS entry satisfies it. */
export interface ScanModelRegistryEntry {
  key: string;
  displayName: string;
  costPer1000: number;
}

export enum ScanMode {
  Fixed = 'fixed',
  Any = 'any',
  All = 'all',
  Multi = 'multi',  // comma-separated list: A/B random pick + sequential fallback
}

export interface ModelOption {
  key: string;
  label: TranslatedString;
}

export interface ScanConfig {
  scanMode: ScanMode;
  fixedModel: string;
  modelOptions: ModelOption[];
  selectedModel: string;
  multiModels: string[];
}

/**
 * Parse a *ScanProvider config value into a structured ScanConfig.
 * @param configValue The raw config string (e.g. 'gemini-2.5-flash', 'all', 'any', 'model1,model2')
 * @param defaultModel Default model key if config is empty
 * @param lastSelectedModel Last user-selected model (for 'any' mode restore)
 * @param models Model registry to use when building the 'any'-mode picker. Defaults to SCAN_MODELS.
 */
export function parseScanConfig(
  configValue: string | undefined,
  defaultModel: string = DEFAULT_SCAN_MODEL,
  lastSelectedModel?: string,
  models: ScanModelRegistryEntry[] = SCAN_MODELS,
): ScanConfig {
  const value = configValue || defaultModel;

  if (value === 'all') {
    return { scanMode: ScanMode.All, fixedModel: defaultModel, modelOptions: [], selectedModel: '', multiModels: [] };
  }

  if (value === 'any') {
    const modelOptions: ModelOption[] = [
      { key: 'all', label: LL.common.scanModels.allModels() },
      ...models.map(m => ({
        key: m.key,
        // m.displayName is the AI vendor's model name (e.g. "Gemini 2.5 Flash") -- a product
        // name, not catalog prose, so it is inserted as a template param rather than translated.
        label: LL.common.scanModels.costEstimate({ displayName: m.displayName, cost: String(m.costPer1000) }),
      })),
    ];
    const selectedModel = lastSelectedModel || 'all';
    return { scanMode: ScanMode.Any, fixedModel: defaultModel, modelOptions, selectedModel, multiModels: [] };
  }

  const multi = value.split(',').map(k => k.trim()).filter(Boolean);
  if (multi.length > 1) {
    return { scanMode: ScanMode.Multi, fixedModel: defaultModel, modelOptions: [], selectedModel: '', multiModels: multi };
  }

  return { scanMode: ScanMode.Fixed, fixedModel: value, modelOptions: [], selectedModel: '', multiModels: [] };
}

// resolve the model key to send to the server from any scan config
// for All mode, returns 'all' (server runs all models in parallel)
// for Multi mode, picks a random model from the list
export function resolveModelKey(config: ScanConfig): string {
  switch (config.scanMode) {
    case ScanMode.All:
      return 'all';
    case ScanMode.Any:
      return config.selectedModel;
    case ScanMode.Multi:
      return config.multiModels[Math.floor(Math.random() * config.multiModels.length)] || config.fixedModel;
    case ScanMode.Fixed:
      return config.fixedModel;
  }
}
